export type SegmentTraits = {
  name: string;
  email: string;
  hasAccess?: boolean;
  hasEverLoggedIn?: boolean;
};

export enum SegmentEventType {
  SYSTEM_ERROR_RECEIVED = 'systemErrorReceived',
  LINK_SENT = 'linkSent',
  ADVISOR_QUOTE_TOOL_USED = 'advisorQuoteToolUsed',
  VST_INTERACTED = 'vstInteracted',
  COMMISSION_CHANGE = 'commissionChange',
  CLEAR_FORM = 'clearAllData',
  USE_SEARCH_FIELD = 'useSearchField',
  GENERIC_LINK_ACTION = 'genericLinkAction',
  VIEW_APPLICATION = 'viewApplication',
  DUPLICATE_APPLICATION = 'duplicateApplication',
  ACCOUNT_CENTER_INTERACTED = 'accountCenterInteracted',
  APPLICATION_EDITED = 'applicationEdited',
  CONCEPT_ILLUSTRATION_CALCULATED = 'conceptIllustrationCalculated',
  CONCEPT_ILLUSTRATION_DOWNLOAD_SUPPLEMENTAL_ILLUSTRATION = 'conceptIllustrationDownloadSupplementalIllustration',
  VIEW_POLICY = 'viewPolicy',
  ACCOUNT_CENTER_INTERACTED_CHANGEOFPAYMENTFREQUENCY = 'accountCenterInteractedChangeOfPaymentFrequency',
  ACCOUNT_CENTER_INTERACTED_CHANGEOFADDRESS = 'accountCenterInteractedChangeOfAddress',
  UNAUTHORIZED_GET_PII_KEY_BY_VALUE = 'unauthorizedGetPiiKeyByValue',
  UNAUTHORIZED_PREPARE_APPLICATION_FOR_EDIT = 'unauthorizedPrepareApplicationForEdit',
  UNAUTHORIZED_PREPARE_APPLICATION_READ_ONLY = 'unauthorizedPrepareApplicationReadOnly',
  EMAIL_SURVEY_RESPONSE_RECORDED = 'emailSurveyResponseRecorded',
}

export enum SegmentEventAction {
  PERSONALIZED_LINK_SENT = 'personalized',
  ADVISOR_ASSISTED_APP_LINK_SENT = 'advisor_assisted',
  ADVISOR_ASSISTED_APP_STARTED = 'aaa_started',
  BI_GENERATED = 'basic_illustration_generated',
  GUARANTEED_CASH_SELECTED = 'guaranteed_cash_value_selected',
  GUARANTEED_COVERAGE_SELECTED = 'guaranteed_coverage_amount_selected',
  INVESTED_AMOUNT_SELECTED = 'invested_amount_selected',
  AGE_SELECTED = 'age_selected',
  DEFAULT_COMMISSION_SPLIT = 'change_default',
  SHARE_PAGE_COMMISSION_SPLIT = 'set_on_share_page',
  CLIENT_DETAILS_PAGE_COMMISSION_SPLIT = 'update_from_client_details',
  CLEAR_FORM = 'clear_all_data',
  SEARCH_KEYWORD = 'type_in_search',
  SEARCH_RESULT_SELECTED = 'select_search_result',
  CLEAR_SEARCH_TEXTBOX = 'clear_search_textbox',
  CLEAR_SEARCH_RESULT = 'clear_search_result',
  GENERIC_LINK_COPIED = 'generic_link_copied',
  GENERIC_LINK_READY_TO_SEND = 'generic_link_ready_to_send_button_clicked',
  VIEW_APPLICATION_ACTION = 'view_application',
  VIEW_POLICY_ACTION = 'view_policy',
  RETURN_TO_DETAILS = 'return_to_customer_details',
  DUPLICATE_APPLICATION = 'duplicate_application',
  LAB_PREFERENCE_SELECTED = 'lab_preference_selected',
  KEBAB_MENU_SELECTED = 'kebab_menu_selected',
  EXPORT_DOWNLOADED = 'export_downloaded',
  POLICY_PACKET_DOWNLOADED = 'policy_packet_downloaded',
  APP_PACKET_DOWNLOADED = 'app_packet_downloaded',
  POLICY_CONVERSION_SUBMITTED = 'policy_conversion_submitted',
  POLICY_CONVERSION_MODAL_OPENED = 'policy_conversion_modal_opened',
  ADVISOR_SELECTED = 'advisor_selected',
  SHORTCUT_KEY_PRESSED = 'shortcut_key_pressed',
  SHORTCUT_MENU_OPENED = 'shortcut_menu_opened',
  APPLICATION_EDITED = 'application_edited',
  CONCEPT_ILLUSTRATION_CALCULATED = 'concept_illustration_calculated',
  CONCEPT_ILLUSTRATION_DOWNLOAD_SUPPLEMENTAL_ILLUSTRATION = 'concept_illustration_download_supplemental_illustration',
  VPQ_ERRORS_DETECTED = 'vpq_errors_detected',
  CASE_FILTER_CHANGED = 'case_filter_changed',
  CASE_PAGINATION_CHANGED = 'case_pagination_changed',
  CASE_FILTER_USED = 'case_filter_used',
  CASE_FILTER_REUSED = 'case_filter_reused',
  ADVISOR_CHANGED_OWNER_ADDRESS = 'advisor_changed_owner_address',
  ADVISOR_CHANGED_PAYMENT_FREQUENCY = 'advisor_changed_payment_frequency',
  IS_HELPFUL_EMAIL_SURVEY_RESPONSE_RECORDED = 'is_helpful_email_survey_response_recorded',
  NONE = '',
}

export type DefaultAdvisorSiteSegmentProperties = {
  environment: string;
  producer_id: string;
  proxy_id: string;
};

export type AdvisorLoginFailedSegmentProperties = DefaultAdvisorSiteSegmentProperties & {
  error_type: string;
  error_reason: string;
  error_description: string;
  okta_user_name: string;
};

export type LinkSentSegmentProperties = DefaultAdvisorSiteSegmentProperties & {
  link_type: string; //personalized, advisor_assisted
  customer_id: string;
  primary_term_length: number;
  primary_face_amount: number;
  primary_product: string;
  secondary_term_length: number;
  secondary_face_amount: number;
  secondary_product: string;
  insured_age: number;
  owner_state: string;
  insured_gender: string;
  payment_frequency: string;
  rate_class: string; //rate class code as in '1','2','3','4','5'
  gir_face_amount: number;
  rtr_face_amount: number;
  primary_waiver: boolean;
  secondary_waiver: boolean;
  quote_use_case: string;
  owner_equals_insured: boolean;
  owner_entity_name: string;
  application_source_type: string;
  dividendInterestAdjustment: number;
  showPremiumToClient: boolean;
};

export type AdvisorQuoteToolSegmentProperties = DefaultAdvisorSiteSegmentProperties & {
  action: string;
  application_number: string;
  primary_term_length: number;
  primary_face_amount: number;
  primary_product: string;
  secondary_term_length: number;
  secondary_face_amount: number;
  secondary_product: string;
  primary_waiver: boolean;
  secondary_waiver: boolean;
  gir_face_amount: number;
  rtr_face_amount: number;
};

export type VSTInteractedSegmentProperties = DefaultAdvisorSiteSegmentProperties & {
  action: string; //basic_illustration_generated, invested_amount_selected, guaranteed_coverage_amount_selected, guaranteed_cash_value_selected, age_selected
  value: string;
};

export type CommissionChangeSegmentProperties = DefaultAdvisorSiteSegmentProperties & {
  action: string; // change_default, set_on_share_page, update_from_client_details
  number_of_agents: number;
  application_number: string;
  FYC_percentage_for_primary: number;
  RYC_percentage_for_primary: number;
};

export type QuotePageClearFormSegmentProperties = DefaultAdvisorSiteSegmentProperties & {
  action: string;
};

export type QuotePageSearchSegmentProperties = DefaultAdvisorSiteSegmentProperties & {
  action: string;
  customer_id: string;
};

export type GenericLinkActionSegmentProperties = DefaultAdvisorSiteSegmentProperties & {
  value: string; //generic_link_copied, generic_link_ready_to_send_button_clicked
};

export type ViewApplicationSegmentProperties = DefaultAdvisorSiteSegmentProperties & {
  action: string;
  source: string;
  application_id: string;
};

export type DuplicateApplicationSegmentProperties = DefaultAdvisorSiteSegmentProperties & {
  action: string;
  original_application_number: string;
  new_application_number: string;
  location: string;
};

export type AccountCenterInteractedSegmentProperties = DefaultAdvisorSiteSegmentProperties & {
  action: string;
  value: string;
};

export type ApplicationEditedSegmentProperties = DefaultAdvisorSiteSegmentProperties & {
  action: string;
  source_application_number: string;
  new_application_number: string;
  location: string;
};

export type ConceptIllustrationCalculatedSegmentProperties = DefaultAdvisorSiteSegmentProperties & {
  action?: string;
  illustrationId: string;
  product: string;
  productCode: string;
  faceamount: number;
  wop: boolean;
  health: string;
  disbursements: boolean;
  startAge: number;
  endAge: number;
  amount: string;
  incomeOption: string;
  dividendOption: string;
  dividendInterestRate: number;
  loanInterestType: string;
  paymentFrequency: string;
};

export type ConceptIllustrationDownloadSupplementalIllustrationSegmentProperties =
  DefaultAdvisorSiteSegmentProperties & {
    action: string;
    product: string;
    isSupplemental: boolean;
  };

export function getSegmentEventAction(action: string): SegmentEventAction {
  switch (action) {
    case 'Set default commission split':
      return SegmentEventAction.DEFAULT_COMMISSION_SPLIT;
    case 'Set commission split':
      return SegmentEventAction.SHARE_PAGE_COMMISSION_SPLIT;
    case 'Manage commission split':
      return SegmentEventAction.CLIENT_DETAILS_PAGE_COMMISSION_SPLIT;
    case 'guaranteedCashValues':
      return SegmentEventAction.GUARANTEED_CASH_SELECTED;
    case 'guaranteedDeathBenefits':
      return SegmentEventAction.GUARANTEED_COVERAGE_SELECTED;
    case 'accumulatedPaidPremium':
      return SegmentEventAction.INVESTED_AMOUNT_SELECTED;
    case 'BI':
      return SegmentEventAction.BI_GENERATED;
    default:
      return SegmentEventAction.NONE;
  }
}

export type ViewPolicySegmentProperties = DefaultAdvisorSiteSegmentProperties & {
  action: string;
  source: string;
  channel: string;
  product?: string;
  productType?: string;
};

export type AccountCenterInteractedChangeOfPaymentModeSegmentProperties = DefaultAdvisorSiteSegmentProperties & {
  action: string;
  currentPayment;
  targetPayment;
};

export type AccountCenterInteractedChangeOfAddressSegmentProperties = DefaultAdvisorSiteSegmentProperties & {
  action: string;
  isOwnerSameAsInsured: boolean;
};

export type EmailSurveyResponseProperties = DefaultAdvisorSiteSegmentProperties & {
  action: string;
  emailKey: string;
  response: string;
};
